import React from "react";
import { useNavigate } from "react-router-dom";
import { formatNumber, formatDate } from "../utils/format";

export default function ProductTable({ products, onSort, sortColumn, sortOrder, marginSortType, onProductDeleted, onEdit, onDelete }) {
  const navigate = useNavigate();
  const showActionsColumn = onEdit || onDelete;

  const getSortIcon = (col) => {
    if (col !== sortColumn) return "↕";
    return sortOrder === "asc" ? "↑" : "↓";
  };

  const getMarginSortIcon = (type) => {
    if (sortColumn !== "margin" || marginSortType !== type) return "↕";
    return sortOrder === "asc" ? "↑" : "↓";
  };

  const calculatePercentageMargin = (sellingPrice, purchasePrice) => {
    if (sellingPrice == null || purchasePrice == null || purchasePrice === 0) {
      return null;
    }
    return ((sellingPrice - purchasePrice) / purchasePrice) * 100;
  };

  const handleRowClick = (productId) => {
    navigate(`/products/${productId}`);
  };

  const handleEdit = (e, product) => {
    e.stopPropagation(); // Prevent row click
    if (onEdit) {
      onEdit(product);
    }
  };

  const handleDelete = (e, product) => {
    e.stopPropagation(); // Prevent row click
    if (onDelete) {
      onDelete(product);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <table className="min-w-full">
        <thead className="bg-gray-100">
          <tr>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
              onClick={() => onSort("brand")}
            >
              Merk {getSortIcon("brand")}
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
              onClick={() => onSort("product_name")}
            >
              Product {getSortIcon("product_name")}
            </th>
            <th
              className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
              onClick={() => onSort("confirmed_stock")}
            >
              Bevestigde Voorraad {getSortIcon("confirmed_stock")}
            </th>
            <th
              className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
              onClick={() => onSort("estimated_stock")}
            >
              Geschatte Voorraad {getSortIcon("estimated_stock")}
            </th>
            <th
              className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
              onClick={() => onSort("purchase_price")}
            >
              Aankoopprijs (incl. korting) {getSortIcon("purchase_price")}
            </th>
            <th
              className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
              onClick={() => onSort("selling_price")}
            >
              Verkoopprijs (incl. btw) {getSortIcon("selling_price")}
            </th>
            <th
              className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
              onClick={() => onSort("selling_price_excl_vat")}
            >
              Verkoopprijs (excl. btw) {getSortIcon("selling_price_excl_vat")}
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
              <div className="flex items-center justify-end gap-2">
                <span>Marge</span>
                <div className="flex gap-1">
                  <button
                    onClick={() => onSort("margin", "percentage")}
                    className="hover:bg-gray-300 px-1 rounded"
                    title="Sorteer op percentage"
                  >
                    {getMarginSortIcon("percentage")}
                  </button>
                  <button
                    onClick={() => onSort("margin", "absolute")}
                    className="hover:bg-gray-300 px-1 rounded"
                    title="Sorteer op absoluut bedrag"
                  >
                    {getMarginSortIcon("absolute")}
                  </button>
                </div>
              </div>
            </th>
            <th
              className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
              onClick={() => onSort("recent_price_date")}
            >
              Laatste voorraadtelling {getSortIcon("recent_price_date")}
            </th>
            {showActionsColumn && (
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                Acties
              </th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {products.length === 0 ? (
            <tr>
              <td colSpan="10" className="px-6 py-4 text-center text-gray-500">
                Geen producten gevonden
              </td>
            </tr>
          ) : (
            products.map((p) => (
              <tr
                key={p.id}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => handleRowClick(p.id)}
              >
                <td className="px-6 py-4 whitespace-nowrap text-gray-600">{p.brand}</td>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                  <div className="flex items-center gap-2">
                    <span
                      className="block max-w-xs truncate"
                      title={p.product_name}
                    >
                      {p.product_name}
                    </span>
                    {!p.is_active && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Inactief
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-gray-600">
                  {formatNumber(p.confirmed_stock, 0)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-gray-600">
                  {formatNumber(p.estimated_stock, 0)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-gray-600">
                  {p.purchase_price_incl_discount ? `€${formatNumber(p.purchase_price_incl_discount)}` : "-"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-gray-600">
                  {p.selling_price_incl_vat ? `€${formatNumber(p.selling_price_incl_vat)}` : "-"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-gray-600">
                  {p.selling_price_excl_vat ? `€${formatNumber(p.selling_price_excl_vat)}` : "-"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-gray-600">
                  {(() => {
                    const sellingPrice = p.selling_price_excl_vat;
                    const purchasePrice = p.purchase_price_incl_discount;
                    const percentageMargin = calculatePercentageMargin(sellingPrice, purchasePrice);
                    const absoluteMargin = (sellingPrice != null && purchasePrice != null) ? sellingPrice - purchasePrice : null;
                    if (percentageMargin != null && absoluteMargin != null) {
                      return `${formatNumber(percentageMargin)}% (€${formatNumber(absoluteMargin)})`;
                    } else if (absoluteMargin != null) {
                      return `€${formatNumber(absoluteMargin)}`;
                    }
                    return "-";
                  })()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-gray-600">
                  {formatDate(p.recent_price_date)}
                </td>
                {showActionsColumn && (
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {onEdit && (
                      <button
                        onClick={(e) => handleEdit(e, p)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        Bewerken
                      </button>
                    )}
                    {onDelete && (
                      <button
                        onClick={(e) => handleDelete(e, p)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Verwijderen
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
